// The words a token's numbers are shown with (issue 1309; decision 5 on issue 1059: the system's own words, "HP" and
// "AC" when it names none). The system's words reach the browser in the snapshot's rules view (`RulesView.labels`, the
// table's OWN system's even when its combat rules are borrowed) and again on `table:rules`; a word the system does not
// name is the generic one here. THIS is the one place the table's generic words live: every surface that labels a
// number (the board's overlays, the hover card, the properties panel, the place dialog, the tracker and the turn order)
// reads them through `useNumberWords`.
import { useMemo } from 'react';
import { NONLETHAL_WORDS, nonlethalStatus, type NumberLabels, type Values, type WoundResource } from '@dragons-table/vtt-shared';
import { useTableState } from './TableState';

/**
 * Hit points in words with the nonlethal count beside them (issue 1502): "HP 10/30", "HP 10/30, 10 nonlethal, Staggered".
 * Only what this viewer holds: a player is sent the count only where they are sent the hit points.
 */
export function hpWords(hp: NonNullable<Values['hp']>, w: Pick<NumberWords, 'health'>): string {
  const status = nonlethalStatus(hp);
  return `${w.health} ${hp.current}/${hp.max}${hp.temp ? ` +${hp.temp} temporary` : ''}${hp.nonlethal ? `, ${hp.nonlethal} nonlethal` : ''}${status ? `, ${NONLETHAL_WORDS[status]}` : ''}`;
}

/** The generic words, for a number the system does not name. */
export const GENERIC_WORDS = { health: 'HP', defence: 'AC', initiative: 'Init', challenge: 'CR' } as const;

export type NumberWords = Record<keyof NumberLabels, string>;

/** The system's words where it names them, else the generic ones. */
export function numberWords(labels: NumberLabels | null | undefined): NumberWords {
  return {
    health: labels?.health ?? GENERIC_WORDS.health,
    defence: labels?.defence ?? GENERIC_WORDS.defence,
    initiative: labels?.initiative ?? GENERIC_WORDS.initiative,
    challenge: labels?.challenge ?? GENERIC_WORDS.challenge,
  };
}

/** The table's words for its tokens' numbers. */
export function useNumberWords(): NumberWords {
  const { systemRules } = useTableState();
  const l = systemRules.labels;
  return useMemo(() => numberWords(l), [l?.health, l?.defence, l?.initiative, l?.challenge]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * The system's resources as the wounds read them (issue 1526): each with its levels, the health marked and the last of a
 * chain marked; empty on a system whose resources have no levels (every d20 system), so nothing is shown.
 */
export function woundRulesOf(resources: ReadonlyArray<{ key: string; label: string; levels?: WoundResource['levels']; end?: true }>, health: string | null): WoundResource[] {
  if (!resources.some((r) => r.levels?.length)) return [];
  return resources.map((r) => ({ key: r.key, label: r.label, ...(r.levels ? { levels: r.levels } : {}), ...(r.end ? { end: true } : {}), ...(r.key === health ? { health: true } : {}) }));
}

/** The table's resources as the wounds read them (issue 1526). */
export function useWoundRules(): WoundResource[] {
  const { systemRules } = useTableState();
  return useMemo(() => woundRulesOf(systemRules.resources, systemRules.health), [systemRules.resources, systemRules.health]);
}

/** The resource a system's health bar shows (issue 1312; `RulesView.health`), by key and its own label; null, hit points. */
export interface HealthResource { key: string; label: string }

/** The table's health resource, from its rules view: the resource the view names as the health, with that resource's label. */
export function useHealthResource(): HealthResource | null {
  const { systemRules } = useTableState();
  const key = systemRules.health;
  const label = systemRules.resources.find((r) => r.key === key)?.label ?? null;
  return useMemo(() => (key && label ? { key, label } : null), [key, label]);
}

/**
 * What the health bar shows of a token (issue 1312): on a system whose health is a resource, that resource where the
 * token carries it with a maximum, labelled by the resource; otherwise the hit points, in the system's word. Null: no bar.
 */
export function healthOf(v: Values, health: HealthResource | null, words: Pick<NumberWords, 'health'>): { current: number; max: number; label: string } | null {
  const r = health ? v.resources?.[health.key] : undefined;
  if (health && r && r.max !== undefined) return { current: r.current, max: r.max, label: health.label };
  return v.hp ? { current: v.hp.current, max: v.hp.max, label: words.health } : null;
}
